import { useContext, useEffect, useState, useRef } from "react";
import {
  getMyProfileLabel,
  isClientIdentificationActive,
  resolveTenantRequirements,
  TENANT_REQUIREMENTS,
  haveValue,
  toast,
} from "../helper/common";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import ApplicationContext from "../context/application/ApplicationContext";
import useTranslator from "../hooks/useTranslator";
import ConsentStepModal from "../pages/onboarding/ConsentStepModal";
import copy from "copy-to-clipboard";
import moment from "moment";
import "moment/locale/es";
import ButtonActionTooltip from "./ErrorModal/ButtonActionTooltip";
import TenantRequirementsFlow from "./TenantRequirements/TenantRequirementsFlow";
import constants from "../Utils/constants";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  emailPrefix,
  getInitials,
  resolveParticipantName,
  getApplicationTitle,
} from "../helper/applicationHelpers";
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Icon,
  Select,
  SkeletonList,
  TextField,
} from "./design";
import ApplicationSelect from "./ApplicationSelect/ApplicationSelect";
import "./shareProfileModal.css";

/* ── small layout pieces (look comes from the shared components) ── */

const StepTitle = ({ number, children }) => (
  <div className="shareProfile__stepTitle">
    <span className="shareProfile__stepNum">{number}</span>
    <h3 className="shareProfile__stepText">{children}</h3>
  </div>
);

/* grey chip: what is shared (my profile or an application) */
const ShareChip = ({ isApplication, label }) => (
  <Badge tone="neutral" className="shareProfile__chip">
    <Icon name={isApplication ? "users" : "user"} size={14} />
    <span className="shareProfile__chipText" title={label}>
      {label}
    </span>
  </Badge>
);

/* one landlord / request row */
const AccessCard = ({ name, badge, action, pending = false, children }) => (
  <div className={`shareProfile__card${pending ? " is-pending" : ""}`}>
    <Avatar name={name} size="md" />
    <div className="shareProfile__cardInfo">
      <div className="shareProfile__nameRow">
        <p className="shareProfile__cardName" title={name}>
          {name}
        </p>
        {badge}
      </div>
      {children}
    </div>
    <div className="shareProfile__cardAction">{action}</div>
  </div>
);

const ShareProfileModal = ({
  isOpen,
  onClose,
  loadLandlords,
  setAllLandlords,
  landlordsOrig,
  setAllLandlordsOrig,
  searchText,
  setSearchText,
  loading1,
  landlords,
  setShowRemove,
  shareProfileFun,
  setRemoveLandlordId,
  onStopSharing,
  processingLandlordId,
  myApplications,
  shareApplicationWithLandlord,
}) => {
  const { _t, i18n } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, loadUserTenant } = authContext;
  const tenantContext = useContext(TenantContext);
  const {
    fullProfileShared,
    loadSharedProfiles,
    declinePendingShare,
    checkLandlordExists,
    sendRequestToLandlordToShareProfile,
    getTenantShareProfileRequestEntries,
    cancelTenantShareProfileRequest,
  } = tenantContext;
  const {
    reloadApplications,
    applicationsLoading,
    landlordRequests,
    landlordRequestsLoading,
    loadLandlordRequests,
    acceptLandlordRequest,
    rejectLandlordRequest,
  } = useContext(ApplicationContext);
  const queryParam = new URLSearchParams(window.location.search);
  const [filteredShortlisted, setFilteredShortlisted] = useState([]);
  const [showConsent, setShowConsent] = useState(false);
  const [
    clientIdentificationShareLandlord,
    setClientIdentificationShareLandlord,
  ] = useState(null);
  const [
    clientIdentificationAcceptRequest,
    setClientIdentificationAcceptRequest,
  ] = useState(null);
  const [selectedLandlord, setSelectedLandlord] = useState({});
  const [email, setEmail] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [emailLandlordData, setEmailLandlordData] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [sendingEmailRequest, setSendingEmailRequest] = useState(false);
  const [pendingShareRequests, setPendingShareRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);
  const [loadingSharedProfiles, setLoadingSharedProfiles] = useState(false);
  const [cancelingRequestId, setCancelingRequestId] = useState(null);
  const emailDebounceRef = useRef(null);

  const [shareSelector, setShareSelector] = useState(null);

  // Steps popup opened from a "Signature pending" badge
  const [signatureSteps, setSignatureSteps] = useState(null);
  const [requestShareSelectors, setRequestShareSelectors] = useState({});
  const [processingRequestId, setProcessingRequestId] = useState(null);

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    if (!emailTouched) return;

    clearTimeout(emailDebounceRef.current);

    emailDebounceRef.current = setTimeout(async () => {
      if (!email || !isValidEmail(email)) {
        setEmailExists(null);
        return;
      }

      try {
        setEmailChecking(true);
        const res = await checkLandlordExists(email);
        if (res?.exists) {
          setEmailExists(true);
          setEmailLandlordData(res);
        } else {
          setEmailExists(false);
          setEmailLandlordData(null);
        }
      } catch {
        setEmailExists(false);
        setEmailLandlordData(null);
      } finally {
        setEmailChecking(false);
      }
    }, 600);

    return () => clearTimeout(emailDebounceRef.current);
  }, [email, emailTouched]);

  const loadPendingShareRequests = async () => {
    setLoadingPendingRequests(true);
    try {
      const res = await getTenantShareProfileRequestEntries(true);
      if (res?.success) {
        setPendingShareRequests(Array.isArray(res?.data) ? res.data : []);
      } else {
        setPendingShareRequests([]);
      }
    } catch (error) {
      console.log(error);
      setPendingShareRequests([]);
    } finally {
      setLoadingPendingRequests(false);
    }
  };

  const loadLatestSharedProfiles = async () => {
    const tenantId = user?._id || localStorage.getItem("saveduserid");
    if (!tenantId) return;

    setLoadingSharedProfiles(true);
    try {
      await loadSharedProfiles(tenantId, "full", true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingSharedProfiles(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      Promise.allSettled([
        loadPendingShareRequests(),
        loadLatestSharedProfiles(),
        reloadApplications(),
      ]);
    }
  }, [isOpen, user?._id]);

  useEffect(() => {
    if (isOpen) {
      const applicationId = new URLSearchParams(window.location.search).get(
        "applicationId"
      );
      if (applicationId && myApplications) {
        const app = myApplications.find((a) => a._id === applicationId);
        // Only applications can be shared (no "my profile only")
        setShareSelector(app ? { type: "application", app } : null);
      } else {
        setShareSelector(null);
      }
    }
  }, [isOpen, myApplications]);

  useEffect(() => {
    setFilteredShortlisted(fullProfileShared);
    setAllLandlords((prev) => [...prev]);
    setAllLandlordsOrig((prev) => [...prev]);
  }, [fullProfileShared]);

  const filterlandlord = (searchText) => {
    const temp = [...landlordsOrig];
    const filtered = temp.filter((val) =>
      `${val?.fname_companyName} ${val?.lname}`
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setAllLandlords(filtered);
  };

  const isPropertyLinked = (landlordid) => {
    for (const profileShared of fullProfileShared) {
      if (
        profileShared.landlordid === landlordid &&
        haveValue(profileShared?.propertyid)
      ) {
        return profileShared;
      }
    }
    return null;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const effect_func = async () => {
        await loadLandlords();

        if (haveValue(queryParam.get("filter"))) {
          const name = queryParam.get("filter");
          setSearchText(name);
          filterlandlord(name);
        }
      };
      if ((searchText ?? "").length > 0) {
        effect_func();
      }
    }, 250);

    return () => clearTimeout(handler);
  }, [searchText]);

  const handlecopyUrl = () => {
    let url = `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/profile-request?sharedProfileLink=${user?._id}`;
    if (shareSelector?.type === "application" && shareSelector?.app?._id) {
      url += `&application_id=${shareSelector.app._id}`;
    }
    copy(url);
    toast.success(_t("linkCopied"));
  };

  const getLandlordEmail = (landlordData) =>
    landlordData?.email ||
    landlordData?.landlord_email ||
    landlordData?.landlord_id?.email;

  // Records a dismissed share decision so the forensic resolver never
  // confirms it. No-op when nothing pending matches.
  const markShareDeclined = (source, landlordData, workspaceRef) => {
    const landlordRef = landlordData?._id ?? landlordData?.landlord_id;
    const landlordId = landlordRef?._id ?? landlordRef;
    const payload = { source };
    if (typeof landlordId === "string" && landlordId) {
      payload.landlordId = landlordId;
    } else {
      const landlordEmail = getLandlordEmail(landlordData);
      if (!landlordEmail) return;
      payload.landlordEmail = landlordEmail;
    }
    const workspaceId = workspaceRef?._id ?? workspaceRef;
    if (typeof workspaceId === "string" && workspaceId) {
      payload.workspaceId = workspaceId;
    }
    declinePendingShare(payload);
  };

  const shouldShowConsent = (landlordData) => {
    // Source of funds (affidavit_activated) is asked through the KYC-AML
    // client identification form, not as a consent here
    return (
      landlordData?.insurance_service_status === true &&
      (!haveValue(user?.consents?.gdpr_consent) ||
        !haveValue(user?.consents?.skor_consent))
    );
  };

  // Landlord (or landlord request) asks for a tenant step: household and pets,
  // or the KYC-AML form while it is not signed
  const shouldShowClientIdentification = (entity) => {
    const requirements = resolveTenantRequirements(
      entity,
      entity?.landlord_id,
      typeof entity?.workspace_id === "object" ? entity.workspace_id : null
    );
    const identificationPending =
      (requirements[TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION].active ||
        isClientIdentificationActive(entity)) &&
      user?.consents?.client_identification_consent?.status !==
        constants.ACCEPTED;
    return (
      identificationPending ||
      requirements[TENANT_REQUIREMENTS.HOUSEHOLD].active
    );
  };

  const sendShareRequest = async (landlordEmail) => {
    if (!landlordEmail) return;

    setSendingEmailRequest(true);
    try {
      const res = await sendRequestToLandlordToShareProfile(
        landlordEmail.trim(),
        shareSelector?.app?._id
      );

      if (res?.success) {
        // Also call the application share endpoint when sharing with an application
        if (shareSelector?.type === "application" && shareSelector?.app?._id) {
          const landlordId = emailLandlordData?._id || selectedLandlord?._id;
          if (landlordId) {
            await shareApplicationWithLandlord(
              landlordId,
              shareSelector.app._id
            ).catch(() => {});
          }
        }

        toast.success(res?.message || _t("requestSentSuccessfully"));
        setEmail("");
        setEmailTouched(false);
        setEmailExists(null);
        setEmailLandlordData(null);
        setSelectedLandlord({});
        reloadApplications();
        await loadPendingShareRequests();
        return;
      }

      const errorMsg = res?.key
        ? `${_t(res.key)}${res.name ? ` ${res.name}` : ""}`
        : res?.message || _t("failedToShareProfile");
      toast.error(errorMsg);
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToShareProfile"));
    } finally {
      setSendingEmailRequest(false);
    }
  };

  const handleLandlordRequestFlow = async (landlordData) => {
    const landlordEmail = getLandlordEmail(landlordData);
    if (!landlordEmail) return;

    if (shouldShowConsent(landlordData)) {
      setSelectedLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      setShowConsent(true);
      return;
    }

    if (shouldShowClientIdentification(landlordData)) {
      setSelectedLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      setClientIdentificationShareLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      return;
    }

    await sendShareRequest(landlordEmail);
  };

  const continueAfterConsent = async (landlordData) => {
    const landlordEmail = getLandlordEmail(landlordData);
    if (!landlordEmail) return;

    if (shouldShowClientIdentification(landlordData)) {
      setClientIdentificationShareLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      return;
    }

    await sendShareRequest(landlordEmail);
  };

  const handleSendShareRequest = async () => {
    if (!isValidEmail(email) || emailChecking || emailExists !== true) {
      return;
    }

    await handleLandlordRequestFlow({
      ...emailLandlordData,
      email: email.trim(),
    });
  };

  const getPendingRequestEmail = (request) =>
    request?.landlord_id?.email ||
    request?.landlord_email ||
    request?.email ||
    request?.landlordEmail ||
    "";

  const getPendingRequestDisplayName = (request) => {
    const fname =
      request?.landlord_id?.fname || request?.landlord_id?.firstname || "";
    const lname =
      request?.landlord_id?.lname || request?.landlord_id?.lastname || "";
    const full = `${fname} ${lname}`.trim();
    if (full) return full;
    const email = getPendingRequestEmail(request);
    return emailPrefix(email) || email;
  };

  // Stop sharing: an application lists everyone it applies to and asks
  // first; an individual profile stops straight away
  const handleStopSharingClick = (sharedProfile) => {
    const application = sharedProfile?.application_id;
    if (!onStopSharing) {
      onClose();
      setShowRemove(true);
      setRemoveLandlordId(sharedProfile?._id);
      return;
    }
    if (!application?._id) {
      onClose();
      onStopSharing(sharedProfile?._id);
      return;
    }

    // Names already loaded: the application list carries each accepted
    // member's guarantors; fall back to the shared row's application
    const fullApplication =
      (myApplications ?? []).find(
        (app) => app?._id?.toString() === application._id.toString()
      ) ?? application;
    const members = (fullApplication.participants ?? []).filter(
      (participant) =>
        !participant?.is_deleted &&
        participant?.consent_status === constants.ACCEPTED
    );
    const isMe = (person) =>
      (person?.id && person.id.toString() === user?._id?.toString()) ||
      person?.email?.toLowerCase() === user?.email?.toLowerCase();
    const youLabel = _t("you").toLowerCase();
    const guarantorLabel = _t("guarantor").toLowerCase();
    const memberNames = members.flatMap((member) => [
      isMe(member) ? youLabel : resolveParticipantName(member),
      ...(member.guarantors ?? []).map(
        (guarantor) =>
          `${isMe(guarantor) ? youLabel : resolveParticipantName(guarantor)} (${guarantorLabel})`
      ),
    ]);

    onClose();
    onStopSharing(sharedProfile?._id, {
      landlordName:
        sharedProfile?.landlord_fname?.trim() ||
        sharedProfile?.landlord_compname ||
        sharedProfile?.landlord_email ||
        "",
      applicationTitle: getApplicationTitle(application, _t),
      memberNames,
    });
  };

  const handleCancelShareRequest = async (requestId) => {
    if (!requestId) return;

    setCancelingRequestId(requestId);
    try {
      const res = await cancelTenantShareProfileRequest(requestId);

      if (res?.success) {
        toast.success(res?.message || _t("requestCancelledSuccessfully"));
        setPendingShareRequests((prev) =>
          prev.filter((request) => request?._id !== requestId)
        );
        reloadApplications().catch(() => {});
        return;
      }

      toast.error(
        res?.key ? _t(res.key) : res?.message || _t("failedToRejectRequest")
      );
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setCancelingRequestId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setShowConsent(false);
      setClientIdentificationShareLandlord(null);
      setClientIdentificationAcceptRequest(null);
      setSelectedLandlord({});

      const url = new URL(window.location.href);
      const hasModalParams =
        url.searchParams.has("shareprofilemodal") ||
        url.searchParams.has("applicationId");

      if (!hasModalParams) return;

      url.searchParams.delete("shareprofilemodal");
      url.searchParams.delete("applicationId");
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    }
  }, [isOpen]);

  useEffect(() => {
    const lang = i18n.language?.toLowerCase();
    const shortLang = lang?.split("-")[0];
    moment.locale(shortLang);
  }, [i18n.language]);

  /* build the application sub-label shown under a landlord's name */
  const getApplicationBadge = (application_data) => {
    if (!application_data) return null;

    const initials = (application_data.participants ?? [])
      .slice(1, 3)
      .map((p) => getInitials(resolveParticipantName(p)))
      .join(" + ");

    return `${application_data.title}${initials ? ` · ${initials}` : ""}`;
  };

  const selectRequestShare = (id, option) => {
    setRequestShareSelectors((prev) => ({ ...prev, [id]: option }));
  };

  const handleAcceptLandlordRequest = async (request) => {
    const shareOption = requestShareSelectors[request._id] ?? null;
    if (!shareOption) return;

    if (shouldShowClientIdentification(request)) {
      setClientIdentificationAcceptRequest(request);
      return;
    }

    await acceptRequestWithShareOption(request, shareOption);
  };

  const acceptRequestWithShareOption = async (request, shareOption) => {
    setProcessingRequestId(request._id);
    try {
      const applicationId =
        shareOption.type === "application" ? shareOption.app?._id : undefined;
      const res = await acceptLandlordRequest(request._id, applicationId);
      if (res?.success) {
        toast.success(res?.message || _t("requestSentSuccessfully"));
        loadSharedProfiles(user?._id, "full", true);
        reloadApplications();
      } else {
        const errorMsg = res?.key
          ? `${_t(res.key)}${res.name ? ` ${res.name}` : ""}`
          : res?.message || _t("failedToShareProfile");
        toast.error(errorMsg);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message;
      if (errMsg) {
        toast.error(_t(errMsg));
      } else {
        toast.error(_t("failedToShareProfile"));
      }
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectLandlordRequest = async (requestId) => {
    setProcessingRequestId(requestId);
    try {
      const res = await rejectLandlordRequest(requestId);
      if (res?.success) {
        reloadApplications();
      } else {
        toast.error(res?.message || _t("failedToRejectRequest"));
      }
    } catch {
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setProcessingRequestId(null);
    }
  };

  const isRefreshingProfileData =
    loading1 ||
    loadingPendingRequests ||
    loadingSharedProfiles ||
    applicationsLoading ||
    landlordRequestsLoading;

  const sendDisabled =
    !shareSelector ||
    !isValidEmail(email) ||
    emailChecking ||
    emailExists !== true;
  const showEmailMissing =
    !emailChecking &&
    emailTouched &&
    emailExists === false &&
    isValidEmail(email);
  const shareTooltipId = !shareSelector ? "button-action-tooltip" : "";
  const peopleCount =
    (pendingShareRequests?.length ?? 0) +
    (landlordRequests?.length ?? 0) +
    (filteredShortlisted?.length ?? 0);

  const sharedLabel = (data) =>
    data?.application_id
      ? (getApplicationBadge(data.application_id) ??
        getMyProfileLabel(user, _t))
      : getMyProfileLabel(user, _t);

  const pendingBadge = <Badge tone="warning">{_t("pending", "Pending")}</Badge>;

  return (
    <Drawer
      open={Boolean(isOpen)}
      onClose={onClose}
      title={_t("shareYourProfile")}
      description={_t(
        "shareProfileSubtitle",
        "Choose what to share and how to send it."
      )}
      closeLabel={_t("close")}
      size="lg"
    >
      <div className="shareProfile">
        <div className="shareProfile__step">
          <StepTitle number={1}>{_t("whatDoYouWantToShare")}</StepTitle>
          <ApplicationSelect
            applications={myApplications}
            value={shareSelector?.app?._id ?? ""}
            onChange={(value, app) =>
              setShareSelector(app ? { type: "application", app } : null)
            }
            onCreate={() => {
              onClose();
              window.dispatchEvent(new CustomEvent("openApplicationsModal"));
            }}
            placeholder={_t(
              "selectWhatToShare",
              "Select what you want to share"
            )}
          />
        </div>

        <div className="shareProfile__step">
          <StepTitle number={2}>
            {_t("howDoYouWantToShareIt", "How do you want to share it?")}
          </StepTitle>

          <div className="shareProfile__how">
            <p className="shareProfile__howTitle">{_t("sendByEmail")}</p>
            <p className="shareProfile__howText">
              {_t(
                "sendViaEmailDescription",
                "The owner will receive an email from Skor with a link to your profile."
              )}
            </p>
            <div className="shareProfile__emailRow">
              <div
                className="shareProfile__emailInput"
                data-tooltip-id={shareTooltipId}
              >
                <TextField
                  id="shareProfileEmail"
                  size="md"
                  type="email"
                  placeholder="owner@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailTouched(true);
                    setEmailExists(null);
                    setEmailLandlordData(null);
                    setEmailChecking(false);
                  }}
                  disabled={!shareSelector}
                  error={showEmailMissing ? _t("landlordDoesNotExist") : false}
                  trailing={
                    emailChecking ? (
                      <span className="ds-spinner" aria-hidden="true" />
                    ) : (
                      !emailChecking &&
                      emailTouched &&
                      emailExists === true &&
                      isValidEmail(email) && (
                        <Icon
                          name="checkCircle"
                          size={20}
                          className="shareProfile__ok"
                        />
                      )
                    )
                  }
                />
              </div>
              <span data-tooltip-id={shareTooltipId}>
                <Button
                  variant="primary"
                  size="lg"
                  iconStart={<Icon name="send" size={18} />}
                  loading={sendingEmailRequest}
                  disabled={sendDisabled}
                  onClick={handleSendShareRequest}
                >
                  {sendingEmailRequest ? _t("sending") : _t("send")}
                </Button>
              </span>
            </div>
          </div>

          <div className="shareProfile__copy">
            {_t(
              "orShareHowYouPrefer",
              "Or share it however you prefer by copying the link"
            )}
            <span data-tooltip-id={shareTooltipId}>
              <Button
                variant="link"
                size="sm"
                iconEnd={<Icon name="link" size={14} />}
                disabled={!shareSelector}
                onClick={handlecopyUrl}
              >
                {_t("here", "here")}
              </Button>
            </span>
          </div>
        </div>

        <hr className="shareProfile__divider" />

        <div className="shareProfile__access">
          {!haveValue(searchText) && (
            <div className="shareProfile__listHead">
              <h3 className="shareProfile__listTitle">
                {_t("peopleWithAccess")}
              </h3>
              <Badge tone="neutral">
                <Icon name="users" size={14} />
                {peopleCount} {_t("people")}
              </Badge>
            </div>
          )}

          {isRefreshingProfileData && (
            <SkeletonList
              rows={3}
              avatarSize={36}
              variant="card"
              label={_t("loading")}
            />
          )}

          <div className="shareProfile__list">
            {/* ── Landlord pending requests ── */}
            {!isRefreshingProfileData &&
              (landlordRequests ?? []).map((request) => {
                const landlord = request.landlord_id ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const shareOption = requestShareSelectors[request._id] ?? null;
                const isProcessing = processingRequestId === request._id;
                const hasSelection = shareOption !== null;
                const selectValue = hasSelection
                  ? (shareOption.app?._id ?? "")
                  : "";

                return (
                  <AccessCard
                    key={request._id}
                    pending
                    name={landlordName}
                    badge={pendingBadge}
                    action={
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          iconOnly
                          iconStart={<Icon name="check" size={16} />}
                          aria-label={_t("accept")}
                          disabled={isProcessing || !hasSelection}
                          onClick={() => handleAcceptLandlordRequest(request)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          iconOnly
                          iconStart={<Icon name="close" size={16} />}
                          aria-label={_t("reject")}
                          disabled={isProcessing}
                          onClick={() =>
                            handleRejectLandlordRequest(request._id)
                          }
                        />
                      </>
                    }
                  >
                    <Select
                      id={`shareProfileRequest-${request._id}`}
                      size="sm"
                      aria-label={_t(
                        "selectWhatToShare",
                        "Select what you want to share"
                      )}
                      placeholder={_t(
                        "selectWhatToShare",
                        "Select what you want to share"
                      )}
                      value={selectValue}
                      options={(myApplications ?? []).map((app) => ({
                        value: app._id,
                        label: getApplicationTitle(app, _t),
                      }))}
                      onChange={(e) => {
                        const value = e.target.value;
                        const app = (myApplications ?? []).find(
                          (a) => a._id === value
                        );
                        selectRequestShare(
                          request._id,
                          app ? { type: "application", app } : null
                        );
                      }}
                    />
                  </AccessCard>
                );
              })}

            {/* ── People who already have access ── */}
            {!isRefreshingProfileData &&
              Array.isArray(filteredShortlisted) &&
              !haveValue(searchText) &&
              filteredShortlisted.length > 0 &&
              filteredShortlisted?.map((landlord) => {
                const name =
                  `${landlord?.landlord_fname ?? ""} ${landlord?.landlord_lname ?? ""}`.trim() ||
                  emailPrefix(landlord?.landlord_email ?? "");
                const notMine = landlord?.is_shared_by_me === false;
                // the landlord waits for this tenant's KYC-AML signature
                const signaturePending =
                  (landlord?.landlord_client_identification ||
                    landlord?.application_id?.client_identification_required) &&
                  user?.consents?.client_identification_consent?.status !==
                    constants.ACCEPTED;
                return (
                  <AccessCard
                    key={landlord?._id}
                    name={name}
                    action={
                      notMine ? (
                        <>
                          <span
                            data-tooltip-id={`shared-by-${landlord?._id}`}
                            className="shareProfile__tip"
                          >
                            <Button variant="secondary" size="sm" disabled>
                              {_t("stopSharing", "stopSharing")}
                            </Button>
                          </span>
                          <ReactTooltip
                            id={`shared-by-${landlord?._id}`}
                            place="top"
                            positionStrategy="fixed"
                            noArrow
                            className="react-tooltip-styling"
                            style={{ maxWidth: "16.25rem" }}
                            render={() => (
                              <p>
                                {_t("thisWasSharedBy", "This was shared by")}{" "}
                                {landlord?.shared_by_name ||
                                  _t("yourFlatmate", "your flatmate")}
                              </p>
                            )}
                          />
                        </>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleStopSharingClick(landlord)}
                        >
                          {_t("stopSharing", "stopSharing")}
                        </Button>
                      )
                    }
                  >
                    <ShareChip
                      isApplication={Boolean(landlord?.application_id)}
                      label={sharedLabel(landlord)}
                    />
                    {signaturePending && (
                      <button
                        type="button"
                        className="shareProfile__signatureBtn"
                        onClick={() =>
                          setSignatureSteps({
                            landlordId: landlord?.landlordid,
                            applicationId:
                              landlord?.application_id?._id ?? null,
                          })
                        }
                      >
                        <Badge tone="warning">
                          <Icon name="warning" size={14} />
                          {_t("signaturePending")}
                        </Badge>
                      </button>
                    )}
                  </AccessCard>
                );
              })}

            {/* ── Requests I sent that are still pending ── */}
            {!isRefreshingProfileData &&
              pendingShareRequests?.length > 0 &&
              pendingShareRequests.map((request) => {
                const requestId = request?._id;
                const requestEmail = getPendingRequestEmail(request);
                const isCanceling = cancelingRequestId === requestId;

                return (
                  <AccessCard
                    key={requestId || requestEmail}
                    name={getPendingRequestDisplayName(request)}
                    badge={pendingBadge}
                    action={
                      request?.is_own_request === false ? (
                        <>
                          <span
                            data-tooltip-id={`sent-by-${requestId}`}
                            className="shareProfile__tip"
                          >
                            <Button variant="secondary" size="sm" disabled>
                              {_t("cancel", "Cancel")}
                            </Button>
                          </span>
                          <ReactTooltip
                            id={`sent-by-${requestId}`}
                            place="top"
                            positionStrategy="fixed"
                            noArrow
                            className="react-tooltip-styling"
                            style={{ maxWidth: "16.25rem" }}
                            render={() => (
                              <p>
                                {_t(
                                  "thisRequestWasSentBy",
                                  "This request was sent by"
                                )}{" "}
                                {request?.sent_by_name ||
                                  _t("yourFlatmate", "your flatmate")}
                              </p>
                            )}
                          />
                        </>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCancelShareRequest(requestId)}
                          disabled={isCanceling}
                        >
                          {isCanceling
                            ? `${_t("canceling", "Canceling")}...`
                            : _t("cancel", "Cancel")}
                        </Button>
                      )
                    }
                  >
                    <ShareChip
                      isApplication={Boolean(request?.application_id)}
                      label={sharedLabel(request)}
                    />
                  </AccessCard>
                );
              })}

            {!isRefreshingProfileData &&
              landlordRequests?.length === 0 &&
              pendingShareRequests?.length === 0 &&
              filteredShortlisted.length === 0 && (
                <p className="shareProfile__empty">
                  {_t("youHaventSharedYourProfileWithAnyLandlordOrAgentYet")}
                </p>
              )}
          </div>
        </div>
      </div>

      {showConsent && (
        <ConsentStepModal
          user={user}
          isPopup={true}
          onClose={() => {
            markShareDeclined(
              "consent-close",
              selectedLandlord,
              selectedLandlord?.workspace_id
            );
            setShowConsent(false);
            setSelectedLandlord({});
          }}
          loadSharedProfiles={(tenantId, profiletype) =>
            loadSharedProfiles(tenantId, profiletype, true)
          }
          loadUserTenant={loadUserTenant}
          selectedLandlord={selectedLandlord}
          shareProfileWithLandlord={() =>
            continueAfterConsent(selectedLandlord)
          }
        />
      )}
      {signatureSteps && (
        <TenantRequirementsFlow
          user={user}
          mode="after"
          applicationId={signatureSteps.applicationId}
          landlordId={signatureSteps.landlordId}
          workspace={user?.workspaceId}
          requireIdentification
          onIdentificationSigned={async () => {
            try {
              await loadUserTenant("tenant");
            } catch (error) {
              console.log(error);
            }
          }}
          onClose={() => setSignatureSteps(null)}
        />
      )}
      {clientIdentificationShareLandlord && (
        <TenantRequirementsFlow
          user={user}
          mode="before"
          landlordEmail={clientIdentificationShareLandlord.email}
          applicationId={
            shareSelector?.type === "application"
              ? shareSelector?.app?._id
              : null
          }
          requireIdentification={isClientIdentificationActive(
            clientIdentificationShareLandlord
          )}
          onIdentificationSigned={async () => {
            try {
              await loadUserTenant("tenant");
            } catch (error) {
              console.log(error);
            }
          }}
          onShare={async () => {
            const landlordEmail = getLandlordEmail(
              clientIdentificationShareLandlord
            );
            setClientIdentificationShareLandlord(null);
            await sendShareRequest(landlordEmail);
          }}
          onClose={() => {
            markShareDeclined(
              "before-sharing-close",
              clientIdentificationShareLandlord,
              clientIdentificationShareLandlord?.workspace_id
            );
            setClientIdentificationShareLandlord(null);
            setSelectedLandlord({});
          }}
        />
      )}
      {clientIdentificationAcceptRequest && (
        <TenantRequirementsFlow
          user={user}
          mode="before"
          landlordEmail={clientIdentificationAcceptRequest.landlord_id?.email}
          identificationLandlordEmail={
            isClientIdentificationActive(
              clientIdentificationAcceptRequest.landlord_id
            )
              ? clientIdentificationAcceptRequest.landlord_id?.email
              : null
          }
          workspaceId={clientIdentificationAcceptRequest.workspace_id?._id}
          workspace={
            isClientIdentificationActive(
              clientIdentificationAcceptRequest.landlord_id
            )
              ? undefined
              : clientIdentificationAcceptRequest.workspace_id
          }
          applicationId={
            requestShareSelectors[clientIdentificationAcceptRequest._id]
              ?.type === "application"
              ? requestShareSelectors[clientIdentificationAcceptRequest._id]
                  ?.app?._id
              : null
          }
          requireIdentification={isClientIdentificationActive(
            clientIdentificationAcceptRequest
          )}
          onIdentificationSigned={async () => {
            try {
              await loadUserTenant("tenant");
            } catch (error) {
              console.log(error);
            }
          }}
          onShare={async () => {
            const request = clientIdentificationAcceptRequest;
            const shareOption = requestShareSelectors[request._id] ?? null;
            setClientIdentificationAcceptRequest(null);
            if (shareOption) {
              await acceptRequestWithShareOption(request, shareOption);
            }
          }}
          onClose={() => {
            markShareDeclined(
              "before-sharing-close",
              clientIdentificationAcceptRequest,
              clientIdentificationAcceptRequest?.workspace_id
            );
            setClientIdentificationAcceptRequest(null);
          }}
        />
      )}
      {!shareSelector && (
        <ButtonActionTooltip title="pleaseSelectWhatToShareFirst" />
      )}
    </Drawer>
  );
};

export default ShareProfileModal;
